// Editable taxonomy tree used on the Draft tab.
// Each node shows a right-click context menu for add child / rename / delete operations.
// Manages a mutable local copy of the tree; parent page handles API persistence.

import { useState, useCallback } from 'react'
import { ChevronRight, ChevronDown, Plus, Trash2, Pencil } from 'lucide-react'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu'
import { derivePath, type TreeNode } from './types'

interface DraftTreeEditorProps {
  roots: TreeNode[]
  onChange: (roots: TreeNode[]) => void
  onAddChild: (parentNode: TreeNode) => void
  onRenameNode?: (node: TreeNode) => void
  onDeleteNode: (node: TreeNode) => void
}

export function DraftTreeEditor({
  roots,
  onChange: _onChange,
  onAddChild,
  onRenameNode,
  onDeleteNode,
}: DraftTreeEditorProps) {
  // Track expanded node IDs. Default: all depth-0 nodes expanded.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const root of roots) {
      if (root.children.length > 0) {
        initial.add(root.id)
      }
    }
    return initial
  })

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  // Auto-expand parent when adding a child
  const handleAddChild = useCallback(
    (node: TreeNode) => {
      setExpanded((prev) => {
        const next = new Set(prev)
        next.add(node.id)
        return next
      })
      onAddChild(node)
    },
    [onAddChild],
  )

  if (roots.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No taxonomy nodes. This should not happen — contact support.
      </div>
    )
  }

  return (
    <div className="space-y-0.5">
      {roots.map((root) => (
        <DraftNodeRow
          key={root.id}
          node={root}
          siblings={roots}
          expanded={expanded}
          onToggle={toggle}
          onAddChild={handleAddChild}
          onRenameNode={onRenameNode}
          onDeleteNode={onDeleteNode}
        />
      ))}
    </div>
  )
}

interface DraftNodeRowProps {
  node: TreeNode
  siblings: TreeNode[]
  expanded: Set<string>
  onToggle: (id: string) => void
  onAddChild: (parentNode: TreeNode) => void
  onRenameNode?: (node: TreeNode) => void
  onDeleteNode: (node: TreeNode) => void
}

function DraftNodeRow({ node, siblings, expanded, onToggle, onAddChild, onRenameNode, onDeleteNode }: DraftNodeRowProps) {
  const hasChildren = node.children.length > 0
  const isExpanded = expanded.has(node.id)
  const isRoot = node.depth === 0
  const indent = node.depth * 24

  return (
    <div>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={`flex items-center gap-1.5 rounded-sm py-1 pr-2 hover:bg-accent/50 ${
              node.depth === 0 ? 'font-semibold' : ''
            } ${node.depth === 1 ? 'text-sm font-medium' : ''} ${
              node.depth >= 2 ? 'text-sm text-muted-foreground' : ''
            }`}
            style={{ paddingLeft: `${indent + 4}px` }}
          >
            {/* Expand/collapse chevron */}
            {hasChildren ? (
              <button
                onClick={() => onToggle(node.id)}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm hover:bg-muted"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="w-5 shrink-0" />
            )}

            {/* Path segment — derived from sibling position */}
            <span className="w-10 shrink-0 text-xs tabular-nums text-muted-foreground/60">
              {derivePath(node, siblings)}
            </span>

            {/* Name */}
            <span>{node.name}</span>
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent>
          <ContextMenuItem onClick={() => onAddChild(node)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Child
          </ContextMenuItem>
          {!isRoot && (
            <>
              {onRenameNode && (
                <ContextMenuItem onClick={() => onRenameNode(node)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Rename
                </ContextMenuItem>
              )}
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => onDeleteNode(node)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete{hasChildren ? ` (and ${countDescendants(node)} children)` : ''}
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <DraftNodeRow
              key={child.id}
              node={child}
              siblings={node.children}
              expanded={expanded}
              onToggle={onToggle}
              onAddChild={onAddChild}
              onRenameNode={onRenameNode}
              onDeleteNode={onDeleteNode}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** Count total descendants of a node (for delete confirmation message). */
function countDescendants(node: TreeNode): number {
  let count = 0
  for (const child of node.children) {
    count += 1 + countDescendants(child)
  }
  return count
}
